'use client';

import { useTranslations } from 'next-intl';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import {
  getMaxPlanTerms,
  getMaxYearlySaving,
  type MaxPlanKey,
  type MaxStoreConfig,
} from 'content/products/max';

/**
 * The one place MAX plan copy is chosen. Surfaces never pick words by
 * `MaxPlanKey` or by store: IN sells one-time 3-month / 1-year plans under the
 * same keys, so label, suffix, badge, bullets and the entry price all come from
 * `getMaxPlanTerms`. Keys are literal so `i18n:check-keys` sees every one.
 */
export const useMaxPlanCopy = () => {
  const t = useTranslations('cart');
  const formatPrice = useFormatPrice();

  /** "Monthly" | "3 months" | "1 year" */
  const label = (cfg: MaxStoreConfig, plan: MaxPlanKey): string => {
    const { labelKey, months } = getMaxPlanTerms(cfg, plan);
    switch (labelKey) {
      case 'monthly':
        return t('sharedCart.max.plan.monthly');
      case 'year':
        return t('sharedCart.max.plan.year');
      default:
        return t('sharedCart.max.plan.months', { count: months });
    }
  };

  /** "/mo" | "/yr" | "/3 mo" | "" for one-time plans */
  const suffix = (cfg: MaxStoreConfig, plan: MaxPlanKey): string => {
    const { suffixKey, months } = getMaxPlanTerms(cfg, plan);
    switch (suffixKey) {
      case 'perMonth':
        return t('sharedCart.cartUi.perMonth');
      case 'perYear':
        return t('sharedCart.cartUi.perYear');
      case 'perMonths':
        return t('sharedCart.cartUi.perMonths', { count: months });
      default:
        return '';
    }
  };

  /** "$99.99/yr", "₹2,399" */
  const priceLabel = (
    cfg: MaxStoreConfig,
    plan: MaxPlanKey,
    currencySymbol: string,
  ): string =>
    `${currencySymbol}${formatPrice(cfg.plans[plan].price)}${suffix(cfg, plan)}`;

  /**
   * The cycle price spread per month ("₹666.58/mo" for a yearly plan), or null
   * for one-time plans, which never quote per month. Omit the symbol when the
   * caller renders it as a node (the funnel does).
   */
  const perMonthEquivalentLabel = (
    cfg: MaxStoreConfig,
    plan: MaxPlanKey,
    currencySymbol = '',
  ): string | null => {
    const terms = getMaxPlanTerms(cfg, plan);
    if (terms.oneTime) return null;
    return `${currencySymbol}${formatPrice(terms.perMonthEquivalent)}${t(
      'sharedCart.cartUi.perMonth',
    )}`;
  };

  /** Cart-line bullet describing how the plan is charged. */
  const billingBullet = (cfg: MaxStoreConfig, plan: MaxPlanKey): string => {
    switch (getMaxPlanTerms(cfg, plan).billingKey) {
      case 'monthly':
        return t('sharedCart.max.bullets.billing.monthly');
      case 'yearly':
        return t('sharedCart.max.bullets.billing.yearly');
      default:
        return t('sharedCart.max.bullets.billing.oneTime');
    }
  };

  /** Yearly badge: "Save 17%" or "Save ₹1,597"; null when there is nothing to save. */
  const yearlySaving = (cfg: MaxStoreConfig, currencySymbol: string): string | null => {
    const saving = getMaxYearlySaving(cfg);
    if (saving.kind === 'amount') {
      return saving.amount > 0
        ? t('sharedCart.max.saveAmount', {
            amount: `${currencySymbol}${formatPrice(saving.amount)}`,
          })
        : null;
    }
    return saving.percent > 0
      ? t('sharedCart.max.save', { percent: saving.percent })
      : null;
  };

  /**
   * The entry price a store leads with: "From ₹2,399" for one-time stores,
   * "Starting at $9.99/mo" for subscriptions. `prefix` and `price` let a
   * surface split them across lines; `text` is the whole phrase.
   */
  const entryPrice = (cfg: MaxStoreConfig, currencySymbol: string) => {
    const { oneTime } = getMaxPlanTerms(cfg, 'monthly');
    const price = priceLabel(cfg, 'monthly', currencySymbol);
    const text = oneTime
      ? t('sharedCart.max.from', { price })
      : t('sharedCart.max.startingAt', { price });
    const prefix = (
      oneTime
        ? t('sharedCart.max.from', { price: '' })
        : t('sharedCart.max.startingAt', { price: '' })
    ).trim();
    return { oneTime, prefix, price, text };
  };

  /** HSA/FSA applies to the recurring short plan only. */
  const showHsaBadge = (cfg: MaxStoreConfig, plan: MaxPlanKey): boolean =>
    plan === 'monthly' && !getMaxPlanTerms(cfg, plan).oneTime;

  return {
    label,
    suffix,
    priceLabel,
    perMonthEquivalentLabel,
    billingBullet,
    yearlySaving,
    entryPrice,
    showHsaBadge,
  };
};
